import { Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormModal } from '../../../components/FormModal'
import { AiSparkleIcon } from '../../../components/icons'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import IFilter from '../../../interfaces/AceleraMagenta/index/IFilter'
import BrilhaMagentaService from '../../../services/BrilhaMagenta/BrilhaMagentaService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService } from '../../../services/User/UserService'
import { BrilhaMagentaAiPromptModal } from './BrilhaMagentaAiPromptModal'
import BrilhaMagentaViewPage from './BrilhaMagentaViewPage'

interface IBrilha {
  id: number
  employee_id: number
  created_by: string
  step: number
  done: boolean
  feedback_type: string
  created_at: string
  updated_at: string
  link_pdf: string
  sector_id: number
  sector: ISector
  employee: {
    id: number
    name: string
    email: string
    birthdate: string
    position: string
    created_at: string
    updated_at: string
  }
}

const INITIAL_FILTER: IFilter = {
  date: { start: '', end: '' },
  manager: '',
  name: '',
  sectorId: 0
}

export default function BrilhaMagentaPage() {
  const modalState = useDisclosure()
  const brilhaMagentaService = new BrilhaMagentaService()
  const [brilhaMagentaId, setBrilhaMagentaId] = useState<number>()
  const [brilhas, setBrilhas] = useState<IBrilha[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filter, setFilter] = useState<IFilter>(INITIAL_FILTER)
  const toast = useAppToast()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const isDirty =
    filter.name !== '' ||
    filter.manager !== '' ||
    filter.sectorId !== 0 ||
    filter.date.start !== '' ||
    filter.date.end !== ''

  const hasRole = useHasRole()
  const permissions = {
    downloadEvaluations: hasRole('download_evaluations_brilha'),
    downloadSectorEvaluations: hasRole('download_sector_evaluations_brilha'),
    deleteEvaluations: hasRole('delete_evaluations_brilha'),
    editAiPrompt: hasRole('edit_brilha_ai_prompt')
  }
  const aiPromptModal = useDisclosure()
  const userService = new UserService()
  const [userSectorsIds, setUserSectorsIds] = useState<number[]>([])
  const sectorService = new SectorService()
  const [sectors, setSectors] = useState<ISector[]>([])

  const refreshTable = () => {
    setLoading(true)
    setLoadError(false)
    brilhaMagentaService
      .list(filter)
      .then((response) => {
        setBrilhas(response.data)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar as avaliações', error)
      })
      .finally(() => setLoading(false))
  }

  const fetchUserSectors = () => {
    userService.getSectors().then((response) => {
      setUserSectorsIds(response.map((sectors) => sectors.id))
    })
  }

  const confirmDelete = (id: number) => setDeletingId(id)

  const handleDelete = async () => {
    if (deletingId === null) {
      return
    }

    setIsDeleting(true)
    try {
      await brilhaMagentaService.delete(deletingId)
      toast.success('Registro excluído')
      setDeletingId(null)
      refreshTable()
    } catch (error) {
      toast.error('Erro ao excluir o registro', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const fetchSectors = () => {
    sectorService.listForSelect().then((response) => {
      setSectors(response)
    })
  }

  useEffect(() => {
    fetchUserSectors()
    fetchSectors()
  }, [])

  // A busca por nome e gestor já chega debounced do `FilterSearch`.
  useEffect(() => {
    refreshTable()
  }, [filter])

  return (
    <>
      <PageTitle
        title="Brilha Magenta"
        description="Avaliações de desempenho dos colaboradores"
        actions={
          permissions.editAiPrompt && (
            <ActionButton
              action="settings"
              label="Prompt da IA"
              icon={<AiSparkleIcon />}
              showLabel
              mr={0}
              onClick={aiPromptModal.onOpen}
            />
          )
        }
      />

      <ActionFab to="/brilha-magenta/new" label="Nova avaliação Brilha Magenta" openInNewTab />

      <FilterBar onClear={() => setFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          label="Nome"
          value={filter.name}
          onChange={(name) => setFilter({ ...filter, name })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Setor"
          value={filter.sectorId}
          onChange={(sectorId) => setFilter({ ...filter, sectorId: Number(sectorId) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          // A listagem trata `0` como "sem filtro de setor", não `''`.
          emptyValue={0}
        />
        <FilterSearch
          label="Gestor"
          value={filter.manager}
          onChange={(manager) => setFilter({ ...filter, manager })}
          placeholder="Buscar por gestor"
        />
        <FilterDateRange label="Data" value={filter.date} onChange={(date) => setFilter({ ...filter, date })} />
      </FilterBar>

      <TableContainer padding="0">
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Colaborador</Th>
              <Th>Cargo</Th>
              <Th>Realizado por</Th>
              <Th>Realizado em</Th>
              <Th>Tipo de feedback</Th>
              <Th>Setor</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={8} />
            ) : loadError ? (
              <TableErrorRow colSpan={8} entity="avaliações" feminine onRetry={refreshTable} />
            ) : brilhas.length === 0 ? (
              <TableEmptyRow colSpan={8} entity="avaliação" feminine />
            ) : (
              brilhas.map((brilha: IBrilha) => (
                <Tr key={brilha.id}>
                  <Td>{brilha.id}</Td>
                  <Td maxW="180px">
                    <Tooltip label={brilha.employee.name}>
                      <Text noOfLines={1}>{brilha.employee.name}</Text>
                    </Tooltip>
                  </Td>
                  <Td maxW="150px">
                    <Tooltip label={brilha.employee.position}>
                      <Text noOfLines={1}>{brilha.employee.position}</Text>
                    </Tooltip>
                  </Td>
                  <Td maxW="150px">
                    <Tooltip label={brilha.created_by}>
                      <Text noOfLines={1}>{brilha.created_by}</Text>
                    </Tooltip>
                  </Td>
                  <Td>{formatDateTime(brilha.created_at, { showSeconds: true })}</Td>
                  <Td>{brilha.feedback_type}</Td>
                  <Td maxW="150px">
                    <Tooltip label={brilha.sector?.name} isDisabled={!brilha.sector?.name}>
                      <Text noOfLines={1}>
                        <EmptyCell value={brilha.sector?.name} />
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Flex>
                      {!!brilha.done && (
                        <>
                          <ActionButton
                            action="view"
                            onClick={() => {
                              modalState.onOpen()
                              setBrilhaMagentaId(brilha.id)
                            }}
                          />
                          {(permissions.downloadEvaluations ||
                            (userSectorsIds.includes(brilha.sector_id) && permissions.downloadSectorEvaluations)) && (
                            <a href={brilha.link_pdf} download="Brilha Magenta.pdf" target="_blank" rel="noreferrer">
                              <ActionButton action="download" />
                            </a>
                          )}
                        </>
                      )}
                      {!brilha.done && <ActionButton action="edit" to={`/brilha-magenta/${brilha.id}`} />}
                      {permissions.deleteEvaluations && (
                        <ActionButton
                          action="delete"
                          onClick={() => {
                            confirmDelete(brilha.id)
                          }}
                        />
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <FormModal isOpen={modalState.isOpen} onClose={modalState.onClose} size="wide">
        {brilhaMagentaId !== undefined && (
          <BrilhaMagentaViewPage brilhaMagentaId={brilhaMagentaId} skipLastStep={false} />
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        action="delete"
        entity="brilha magenta"
        isLoading={isDeleting}
      />

      <BrilhaMagentaAiPromptModal isOpen={aiPromptModal.isOpen} onClose={aiPromptModal.onClose} />
    </>
  )
}
